import {useAccountPlaidConnectionQuery} from '@transactions/graphql';
import {DateTime} from 'luxon';
import * as React from 'react';
import {cn} from 'src/lib/utils';

type Props = {
  accountId: string;
  institutionName: string | null | undefined;
  className?: string;
};

/**
 * One-line Plaid connection status for account detail views: institution
 * name, a status dot, and the time of the last successful sync. Fetches
 * status via AccountPlaidConnection — render only for Plaid-linked accounts.
 */
export const PlaidConnectionStatus = ({
  accountId,
  institutionName,
  className,
}: Props) => {
  const {data} = useAccountPlaidConnectionQuery({
    variables: {id: accountId},
  });
  const account = data?.account;
  const connection =
    account?.__typename === 'DepositoryAccount' ||
    account?.__typename === 'InvestmentAccount'
      ? account.plaid
      : null;

  const lastSynced = connection?.lastSuccessfulSyncAt
    ? DateTime.fromISO(connection.lastSuccessfulSyncAt).toRelative()
    : null;
  // connectionActive is null when the access-token record has been deleted —
  // that connection can no longer sync, so anything but an explicit true is
  // inactive. While the status query is still loading (connection == null)
  // the dot renders neutral instead.
  const connectionInactive =
    connection != null && connection.connectionActive !== true;

  return (
    <p
      className={cn(
        'text-muted-foreground m-0 flex items-center gap-1.5',
        className,
      )}
    >
      <span
        className={cn(
          'size-1.5 shrink-0 rounded-full',
          connection == null
            ? 'bg-muted-foreground/40'
            : connectionInactive
              ? 'bg-amber-500'
              : 'bg-emerald-500',
        )}
      />
      <span className="truncate">
        {institutionName ?? 'Bank'} via Plaid
        {connectionInactive && ' · connection inactive'}
        {lastSynced && ` · synced ${lastSynced}`}
      </span>
    </p>
  );
};
